import { useParams, Link } from 'react-router-dom';
import { blogArticles } from '@/mocks/blogArticles';

interface ContentSection {
  h2: string;
  paragraphs: string[];
}

interface ArticleContent {
  intro: string;
  sections: ContentSection[];
}

interface ArticleData {
  slug: string;
  title: string;
  desc: string;
  date: string;
  readTime: string;
  isMain: boolean;
  imageUrl: string;
  keywords: string[];
  content: ArticleContent;
}

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const article = blogArticles.find((a) => a.slug === slug) as ArticleData | undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-950 text-white text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <header className="border-b border-background-200 bg-background-50">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors flex items-center gap-1"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Pandabuy Spreadsheet Hub
          </Link>
          <Link to="/" className="text-lg font-bold tracking-tight text-foreground-950">
            Pandabuy<span className="font-light">Spreadsheet</span>
          </Link>
        </div>
      </header>

      <main>
        <article className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-20">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BlogPosting',
                headline: article.title,
                description: article.desc,
                image: article.imageUrl,
                datePublished: '2026-06-28',
                dateModified: '2026-07-01',
                author: {
                  '@type': 'Organization',
                  name: 'Pandabuy Spreadsheet',
                  url: 'https://pandabuy.website/',
                },
                publisher: {
                  '@type': 'Organization',
                  name: 'Pandabuy Spreadsheet',
                  logo: {
                    '@type': 'ImageObject',
                    url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg',
                  },
                },
                mainEntityOfPage: {
                  '@type': 'WebPage',
                  '@id': `https://pandabuy.website/blog/${article.slug}`,
                },
                keywords: article.keywords.join(', '),
              }),
            }}
          />
          <header className="mb-10">
            <p className="text-accent-600 text-sm font-semibold mb-3">
              {article.keywords[0] || 'Pandabuy Spreadsheet Guide'}
            </p>
            <h1 className="text-3xl md:text-5xl font-extrabold text-foreground-950 leading-[1.15] tracking-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base leading-relaxed mb-6">
              {article.desc}
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-400">
              <span>Published: {article.date}</span>
              <span>&middot;</span>
              <span>{article.readTime}</span>
            </div>
          </header>

          <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
          </div>

          <div className="prose-content">
            <p className="text-foreground-700 leading-relaxed mb-4">
              {article.content.intro}
            </p>

            {article.content.sections.map((section: ContentSection, si: number) => (
              <section key={`sec-${si}`} className="mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {section.h2}
                </h2>
                {section.paragraphs.map((para: string, pi: number) => (
                  <p key={`p-${si}-${pi}`} className="text-foreground-700 leading-relaxed mb-4">
                    {para}
                  </p>
                ))}
                <p className="text-foreground-700 leading-relaxed mb-4">
                  For the complete pandabuy spreadsheet collection and daily updated product data, visit our{' '}
                  <Link to="/" className="text-primary-600 font-medium hover:text-primary-700 underline">
                    pandabuy spreadsheet homepage
                  </Link>
                  {' '}where you can explore all categories and start comparing products today.
                </p>
              </section>
            ))}

            <section className="mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                Frequently Asked Questions
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">
                    How often is the pandabuy spreadsheet updated?
                  </h3>
                  <p className="text-foreground-700 text-sm leading-relaxed">
                    Every pandabuy spreadsheet in our system receives a daily refresh. New products are added, discontinued items are removed, and pricing data is re-verified against current market rates.
                  </p>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">
                    Is the pandabuy sheet free to access?
                  </h3>
                  <p className="text-foreground-700 text-sm leading-relaxed">
                    Yes, the core pandabuy spreadsheet system is freely accessible through our main platform at{' '}
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-primary-600 font-medium hover:text-primary-700 underline"
                    >
                      kaspreadsheet.com
                    </a>
                    . All category sheets are available without any subscription.
                  </p>
                </div>
              </div>
            </section>
          </div>

          <footer className="mt-16 pt-10 border-t border-background-200">
            <div className="flex flex-col items-center text-center gap-4">
              <p className="text-foreground-600 text-sm">
                Ready to transform your shopping experience with data-driven decisions?
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-foreground-950 text-white text-sm font-semibold hover:bg-foreground-800 transition-all whitespace-nowrap"
              >
                <span>Explore More Spreadsheets</span>
                <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white text-xs"></i>
                </span>
              </Link>
              <p className="text-xs text-foreground-400 mt-4">
                &copy; 2026 Pandabuy Spreadsheet. This guide is part of the pandabuy spreadsheet knowledge base.
              </p>
            </div>
          </footer>
        </article>
      </main>
    </div>
  );
}